import React from 'react';
import { Skeleton } from '@/components/ui/skeleton';

// Minimal logo skeleton matching YC aesthetic
const LogoSkeleton: React.FC = () => (
  <div className="flex items-center gap-2">
    <Skeleton className="w-6 h-6 rounded bg-muted" />
    <Skeleton className="h-4 w-24 bg-muted" />
  </div>
);

export const M3RegisterSkeleton: React.FC = () => {
  return (
    <div className="min-h-screen bg-background flex flex-col items-center justify-center p-6">
      <div className="w-full max-w-sm">
        {/* Logo */}
        <div className="flex justify-center mb-12">
          <LogoSkeleton />
        </div>

        {/* Heading */}
        <div className="text-center mb-8">
          <Skeleton className="h-7 w-48 mx-auto mb-2 bg-muted" />
          <Skeleton className="h-4 w-64 mx-auto bg-muted" />
        </div>

        {/* Form fields */}
        <div className="space-y-5">
          <div>
            <Skeleton className="h-4 w-16 mb-2 bg-muted" />
            <Skeleton className="h-11 w-full rounded-md bg-muted" />
          </div>
          <div>
            <Skeleton className="h-4 w-12 mb-2 bg-muted" />
            <Skeleton className="h-11 w-full rounded-md bg-muted" />
          </div>
          <div>
            <Skeleton className="h-4 w-20 mb-2 bg-muted" />
            <Skeleton className="h-11 w-full rounded-md bg-muted" />
          </div>
          
          {/* Button */}
          <Skeleton className="h-11 w-full rounded-md bg-foreground/10 mt-6" />
        </div>

        {/* Footer */}
        <div className="mt-8 flex justify-center">
          <Skeleton className="h-4 w-32 bg-muted" />
        </div>
      </div>
    </div>
  );
};

export const M3InstructionsSkeleton: React.FC = () => {
  return (
    <div className="min-h-screen bg-background flex flex-col items-center justify-center p-6">
      <div className="w-full max-w-md text-center">
        {/* Logo */}
        <div className="flex justify-center mb-12">
          <LogoSkeleton />
        </div>

        {/* Heading */}
        <Skeleton className="h-8 w-56 mx-auto mb-3 bg-muted" />
        <Skeleton className="h-5 w-72 mx-auto mb-8 bg-muted" />

        {/* Instructions list */}
        <div className="text-left space-y-3 mb-10">
          <Skeleton className="h-4 w-full bg-muted" />
          <Skeleton className="h-4 w-5/6 bg-muted" />
          <Skeleton className="h-4 w-4/6 bg-muted" />
        </div>

        {/* Button */}
        <Skeleton className="h-11 w-full rounded-md bg-foreground/10" />

        {/* Back link */}
        <div className="mt-6 flex justify-center">
          <Skeleton className="h-4 w-20 bg-muted" />
        </div>
      </div>
    </div>
  );
};

export const M3AssessmentMainSkeleton: React.FC = () => {
  return (
    <div className="min-h-screen bg-background flex flex-col">
      {/* Header */}
      <div className="p-4 flex items-center justify-between">
        <LogoSkeleton />
        <Skeleton className="h-4 w-16 bg-muted" />
      </div>

      {/* Main content */}
      <div className="flex-1 flex flex-col items-center px-4 py-8">
        {/* Stage title */}
        <Skeleton className="h-6 w-40 mb-2 bg-muted" />
        <Skeleton className="h-4 w-64 mb-6 bg-muted" />

        {/* Progress bar */}
        <div className="w-full max-w-2xl mb-8">
          <div className="flex justify-between mb-2">
            <Skeleton className="h-3 w-24 bg-muted" />
            <Skeleton className="h-3 w-12 bg-muted" />
          </div>
          <Skeleton className="h-2 w-full rounded-full bg-muted" />
        </div>

        {/* Word grid */}
        <div className="w-full max-w-4xl">
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3">
            {Array.from({ length: 20 }).map((_, i) => (
              <Skeleton 
                key={i} 
                className="h-10 rounded-md bg-muted"
                style={{ 
                  animationDelay: `${i * 50}ms`,
                  opacity: 0.6 + (i % 3) * 0.15
                }}
              />
            ))}
          </div>
        </div>

        {/* Bottom controls */}
        <div className="mt-10 flex gap-4">
          <Skeleton className="h-11 w-32 rounded-md bg-muted" />
          <Skeleton className="h-11 w-40 rounded-md bg-foreground/10" />
        </div>
      </div>
    </div>
  );
};

// Loading state for stage transitions
export const M3StageTransitionSkeleton: React.FC = () => {
  return (
    <div className="min-h-screen bg-background flex items-center justify-center">
      <div className="text-center">
        <div className="w-8 h-8 border-2 border-foreground/20 border-t-foreground rounded-full animate-spin mx-auto mb-4" />
        <Skeleton className="h-4 w-32 mx-auto bg-muted" />
      </div>
    </div>
  );
};
